import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ModernButton } from '../ui/ModernButton';
import { SwipeToDelete } from '../ui/SwipeToDelete';
import { FileField } from '../ui/FileField';
import assessmentService, { inviteService } from '../../services/AssessmentService';
import {
  parseInvitationEmail,
  fillPlaceholders,
  composeInvitationBody,
  composeInvitationMarkdown,
  buildMailtoUrl,
  hasOpenPlaceholders,
} from '../../utils/invitationEmail';
import { downloadFile, jobFilename } from '../../utils/jobPackage';
import { inspectQuestionnaire } from '../../utils/questionnaireTracking';
import { withMarkedPlaceholders } from './placeholderPreview';
import { formatSince, inviteActivity, summarizeInvites } from '../../utils/inviteActivity';

/** One success toast that also names what still needs attention before sending. */
function mailOpenedToast(mail, openPlaceholders) {
  const notes = [];
  if (openPlaceholders) notes.push('Im Text steht noch ein Platzhalter — bitte vor dem Senden ersetzen.');
  if (mail.mayBeTruncated) notes.push('Bitte prüfen, ob der Text vollständig ist.');
  toast.success(notes.length ? `Mail geöffnet. ${notes.join(' ')}` : 'Mail geöffnet');
}

const FIELD =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500';

/** One line on where a candidate stands, with the tone that says whether HR should act. */
function describeActivity(invite, now) {
  const activity = inviteActivity(invite, now);
  const quiet = 'text-slate-500 dark:text-slate-400';
  switch (activity.state) {
    case 'submitted':
      return { text: `abgeschickt ${formatDateTime(invite.submittedAt)}`, tone: 'text-emerald-700 dark:text-emerald-400', bar: false };
    case 'inactive':
      return { text: `seit ${activity.idleDays} Tagen inaktiv · ${invite.progress} %`, tone: 'text-amber-700 dark:text-amber-400', bar: true };
    case 'in_progress':
      return {
        text: `in Arbeit · ${invite.progress} % · zuletzt aktiv ${formatSince(invite.lastActiveAt || invite.openedAt, now)}`,
        tone: quiet,
        bar: true,
      };
    case 'opened':
      return { text: `geöffnet ${formatDateTime(invite.openedAt)}`, tone: quiet, bar: false };
    default:
      // "sent" only means the mail programme was opened with the draft, the
      // same limit `submittedAt` above already lives with on the candidate
      // side — the browser cannot see whether it was actually sent.
      return invite.sentAt
        ? { text: `verschickt am ${formatDateTime(invite.sentAt)}`, tone: quiet, bar: false }
        : { text: 'noch nicht geöffnet', tone: quiet, bar: false };
  }
}

/** How often the open dialog re-reads the invitations' tracking fields. */
const INVITE_REFRESH_MS = 10 * 1000;

/** How many invitations to show before folding the rest away. */
const VISIBLE_INVITES = 5;

const MailIcon = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

/** Short German date and time, or nothing when the value is missing. */
function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Tab({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
        active
          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
          : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700/60'
      }`}
    >
      {children}
    </button>
  );
}

/** What the check made of the picked file — the numbers, and what is missing. */
function DocumentReport({ report }) {
  // Drei Fälle, nicht zwei: "geht, aber nicht ganz" darf weder wie ein Fehler
  // aussehen noch wie ein grüner Haken.
  const tone = !report.ok
    ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300'
    : report.problems.length
      ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300'
      : 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300';

  return (
    <div className={`rounded-lg border px-3 py-2 text-xs ${tone}`}>
      <p className="font-semibold">
        {!report.ok
          ? `${report.filename} wurde nicht übernommen`
          : report.problems.length
            ? 'Fragebogen erkannt, mit Einschränkungen'
            : 'Fragebogen erkannt'}
      </p>
      {Boolean(report.questions || report.ratings) && (
        <p className="mt-1 font-mono">
          {report.questions} Fragen ({report.choice} Auswahl, {report.multi} Mehrfach, {report.puzzle} Rätsel,{' '}
          {report.text} Text) · {report.ratings} Skalen · {report.modules} Module
        </p>
      )}
      {report.problems.map((problem) => (
        <p key={problem.text} className="mt-1">
          {problem.level === 'blocker' ? '✕' : '!'} {problem.text}
        </p>
      ))}
    </div>
  );
}

/**
 * The questionnaire a candidate receives after the first two rounds, attached
 * to one job.
 *
 * Split in two, because it holds two jobs that happen at different rates:
 * inviting someone happens per candidate, setting up the document and its mail
 * happens once. Stacked in one scroll they read as four decisions at a time.
 */
export function AssessmentModal({ job, initialTab, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Von wo aus geöffnet wurde: der Knopf meint das Einladen, der Menüeintrag
  // das Dokument. load() schiebt zusätzlich auf "setup", solange nichts da ist.
  const [tab, setTab] = useState(initialTab || 'invites');

  const [record, setRecord] = useState(null);
  const [htmlFile, setHtmlFile] = useState(null);
  const [documentReport, setDocumentReport] = useState(null);
  const [removeDocument, setRemoveDocument] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [invites, setInvites] = useState([]);
  const [showAllInvites, setShowAllInvites] = useState(false);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [deletingInvite, setDeletingInvite] = useState(null);
  const [openingInviteId, setOpeningInviteId] = useState(null);

  /**
   * What the preview shows where the link will go.
   *
   * There is no invitation yet under "Neue Einladung" — nobody has entered an
   * address. A real-looking token here would be a link into nowhere: it was
   * never stored, so it answers "Dieser Link ist ungültig." and reads as a
   * broken feature. The personal link exists only once the invitation does,
   * and the rows below carry the real ones.
   */
  const PREVIEW_LINK = '[persönlicher Link — entsteht beim Versenden]';

  const load = useCallback(async () => {
    setLoading(true);
    const result = await assessmentService.getByJobId(job.id);
    if (!result.success) {
      setError(result.error);
      setTab('setup');
    } else if (result.data) {
      setRecord(result.data);
      setSubject(result.data.emailSubject);
      setBody(result.data.emailBody);
      setError(null);
      const list = await inviteService.listForAssessment(result.data.id);
      setInvites(list.data || []);
    } else {
      // Nothing set up yet, so there is nothing to invite anyone to.
      setError(null);
      setTab('setup');
    }
    setLoading(false);
  }, [job.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Candidates report progress while this dialog is open, so the list follows
  // along instead of only updating when the dialog is opened again.
  useEffect(() => {
    if (!record?.id) return undefined;
    const timer = setInterval(async () => {
      const list = await inviteService.listForAssessment(record.id);
      if (list.success) setInvites(list.data);
    }, INVITE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [record?.id]);

  /**
   * Checks the picked questionnaire before it is accepted at all.
   *
   * A document we cannot read must never end up on a job: a candidate would
   * fill it in, nothing would arrive, and nobody would notice until someone
   * went looking for the answers. Rejecting it here costs one dialog; finding
   * out afterwards costs an application.
   */
  async function handleHtml(file) {
    if (!file) return;
    const parsed = new DOMParser().parseFromString(await file.text(), 'text/html');
    const report = { ...inspectQuestionnaire(parsed), filename: file.name };
    setDocumentReport(report);
    if (!report.ok) {
      toast.error('Diese Datei lässt sich nicht auswerten und wurde nicht übernommen.');
      return;
    }
    setHtmlFile(file);
    setRemoveDocument(false);
  }

  /** Reading the .md fills subject and body, so nobody retypes them. */
  async function handleMarkdown(file) {
    if (!file) return;
    const parsed = parseInvitationEmail(await file.text());
    if (!parsed.ok) {
      toast.error(parsed.error);
      return;
    }
    setSubject(parsed.subject);
    setBody(parsed.body);
    toast.success('Betreff und Text übernommen');
  }

  async function handleSave() {
    if (!record && !htmlFile) {
      toast.error('Bitte zuerst die Fragebogen-HTML auswählen.');
      return;
    }
    setSaving(true);
    const result = await assessmentService.save({
      jobId: job.id,
      label: job.role || '',
      htmlFile,
      removeDocument,
      emailSubject: subject,
      emailBody: body,
      existingId: record?.id,
    });
    setSaving(false);

    if (!result.success) {
      toast.error(result.error);
      return;
    }
    toast.success('Fragebogen 2 gespeichert');
    onSaved?.();
    onClose();
  }

  async function handleCreateInvite({ openMail } = {}) {
    const email = inviteEmail.trim();
    if (!email) {
      toast.error('Bitte eine E-Mail-Adresse angeben.');
      return;
    }
    if (openMail && !inviteFirstName.trim()) {
      toast.error('Für die Mail bitte den Vornamen angeben.');
      return;
    }

    setCreatingInvite(true);
    const result = await inviteService.create({
      assessment: record,
      email,
      firstName: inviteFirstName.trim(),
    });
    setCreatingInvite(false);

    if (!result.success) {
      toast.error(result.error);
      return;
    }
    setInvites((prev) => [result.data, ...prev]);

    if (openMail) {
      const filledBody = composeInvitationBody({
        body,
        link: result.data.link,
        firstName: inviteFirstName.trim(),
        campaignUrl: job.campaignUrl,
      });
      const mail = buildMailtoUrl({ to: email, subject, body: filledBody });
      window.location.href = mail.url;
      mailOpenedToast(mail, hasOpenPlaceholders(`${subject}\n${filledBody}`));
      await markInviteSent(result.data.id);
    } else {
      await navigator.clipboard?.writeText(result.data.link).catch(() => {});
      toast.success('Link erstellt und kopiert');
    }

    setInviteEmail('');
    setInviteFirstName('');
  }

  /**
   * Records that a mail was opened for an invitation, and reflects it in the
   * row immediately. Non-fatal: the mail is already open by this point, so a
   * failure here (most likely `sent_at`/the `sent` status option not yet
   * provisioned) must not look like the send itself failed.
   */
  async function markInviteSent(inviteId) {
    const result = await inviteService.markSent(inviteId);
    if (result.success) {
      setInvites((prev) => prev.map((entry) => (entry.id === inviteId ? result.data : entry)));
    } else {
      console.warn('markSent failed:', result.error);
    }
  }

  /**
   * Re-opens the mail for an invitation that already exists — its stored
   * link and token, never a new invitation.
   *
   * The first name is not always known here: it lives on the invitation only
   * since Addition E, so an invitation created before that (or created here
   * without typing one) has none. The Vorname field covers that gap when HR
   * types a name in before pressing this; when both are empty the greeting
   * keeps the {Vorname} placeholder rather than guessing a name from the
   * mail address.
   */
  async function handleOpenInviteMail(invite) {
    const firstName = invite.firstName || inviteFirstName.trim();
    const filledBody = composeInvitationBody({ body, link: invite.link, firstName, campaignUrl: job.campaignUrl });
    const mail = buildMailtoUrl({ to: invite.email, subject, body: filledBody });
    window.location.href = mail.url;
    mailOpenedToast(mail, hasOpenPlaceholders(`${subject}\n${filledBody}`));

    setOpeningInviteId(invite.id);
    await markInviteSent(invite.id);
    setOpeningInviteId(null);
  }

  async function handleDeleteInvite(invite) {
    setDeletingInvite(invite.id);
    const result = await inviteService.remove(invite.id);
    setDeletingInvite(null);

    if (!result.success) {
      toast.error(result.error);
      return;
    }
    setInvites((prev) => prev.filter((entry) => entry.id !== invite.id));
    toast.success('Einladung gelöscht, der Link ist ungültig');
  }

  async function handlePreview() {
    const result = await assessmentService.getPreviewUrl(record);
    if (!result.success) {
      toast.error(result.error);
      return;
    }
    window.open(result.data, '_blank', 'noopener');
  }

  /**
   * Die hinterlegte HTML als Datei zurück.
   *
   * Nicht unter ihrem gespeicherten Namen: PocketBase hängt beim Hochladen eine
   * Zufallsfolge an (…_fragebogen2_5spg5olsby.html). Heruntergeladen wird unter
   * dem Namen, den die Übergabe und der ZIP-Export benutzen, damit die Datei
   * wieder dorthin passt, wo sie herkam.
   */
  async function handleDownloadDocument() {
    const result = await assessmentService.getPreviewUrl(record);
    if (!result.success) {
      toast.error(result.error);
      return;
    }
    try {
      const response = await fetch(result.data);
      if (!response.ok) throw new Error(String(response.status));
      downloadFile(await response.blob(), jobFilename('assessment', { job }), 'text/html');
    } catch {
      toast.error('Die Datei konnte nicht geladen werden.');
    }
  }

  /**
   * Die Einladungs-Mail als Übergabedatei — mit dem, was hier gerade drinsteht.
   *
   * Dieselbe Form, die das Feld darüber einliest: eine heruntergeladene Datei
   * lässt sich also wieder hochladen.
   */
  function handleDownloadMail() {
    downloadFile(
      composeInvitationMarkdown({ subject, body, title: job.title }),
      jobFilename('invitation', { job }),
      'text/markdown'
    );
  }

  const inviteSummary = summarizeInvites(invites);
  const now = Date.now();
  const shownInvites = showAllInvites ? invites : invites.slice(0, VISIBLE_INVITES);

  // Same composition path as the actual send — composeInvitationBody — so the
  // link lands in the same place. Only the link itself is a stand-in, because
  // this preview is of the template, not of one candidate's mail.
  const previewBody = record?.id
    ? composeInvitationBody({ body, link: PREVIEW_LINK, firstName: inviteFirstName.trim(), campaignUrl: job.campaignUrl })
    : '';
  const previewOpenPlaceholders = record?.id && hasOpenPlaceholders(`${subject}\n${previewBody}`);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
      <div className="w-full max-w-6xl max-h-[92vh] overflow-y-auto rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl">
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 sm:px-8 sm:pt-8">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Fragebogen 2</h2>
            <p className="truncate text-sm text-slate-500 dark:text-slate-400">
              {job.title}
              {job.role && <span className="italic"> · {job.role}</span>}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 pb-6 sm:px-8 sm:pb-8">
          {loading ? (
            <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">Wird geladen …</p>
          ) : (
            <>
              {error && (
                <div className="mb-5 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              <div className="mb-5 flex gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800 w-fit">
                <Tab active={tab === 'invites'} onClick={() => setTab('invites')}>
                  Einladungen{invites.length > 0 ? ` (${invites.length})` : ''}
                </Tab>
                <Tab active={tab === 'setup'} onClick={() => setTab('setup')}>
                  Fragebogen &amp; Mail
                </Tab>
              </div>

              {tab === 'invites' ? (
                !record?.id ? (
                  <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 p-6 text-center">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Noch kein Fragebogen hinterlegt.
                    </p>
                    <button
                      type="button"
                      onClick={() => setTab('setup')}
                      className="mt-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Jetzt einrichten
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                        Neue Einladung
                      </label>
                      <div className="flex flex-wrap items-stretch gap-2">
                        <input
                          type="text"
                          value={inviteFirstName}
                          onChange={(e) => setInviteFirstName(e.target.value)}
                          placeholder="Vorname"
                          className={`${FIELD} sm:w-40`}
                        />
                        <input
                          type="email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          onKeyDown={(e) =>
                            e.key === 'Enter' && !creatingInvite && handleCreateInvite({ openMail: true })
                          }
                          placeholder="bewerber@example.de"
                          className={`${FIELD} flex-1 min-w-56`}
                        />
                        <ModernButton
                          icon={MailIcon}
                          onClick={() => handleCreateInvite({ openMail: true })}
                          disabled={creatingInvite}
                          loading={creatingInvite}
                        >
                          E-Mail-Programm öffnen
                        </ModernButton>
                        <ModernButton
                          variant="secondary"
                          onClick={() => handleCreateInvite({})}
                          disabled={creatingInvite}
                        >
                          Fragebogen 2 erstellen
                        </ModernButton>
                      </div>

                      {previewBody && (
                        <div className="mt-3 space-y-2">
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Betreff: <span className="text-slate-700 dark:text-slate-200">{withMarkedPlaceholders(subject)}</span>
                          </p>
                          <div className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-4 py-3 text-xs text-slate-700 dark:text-slate-200">
                            {withMarkedPlaceholders(previewBody)}
                          </div>
                          {previewOpenPlaceholders && (
                            <p className="text-xs text-amber-700 dark:text-amber-400">
                              In der Mail steht noch ein Platzhalter in geschweiften Klammern. So würde er
                              mitverschickt.
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {invites.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
                          {inviteSummary.total} verschickt · {inviteSummary.opened} geöffnet
                          {inviteSummary.submitted > 0 && ` · ${inviteSummary.submitted} abgeschickt`}
                          {inviteSummary.inactive > 0 && (
                            <span className="text-amber-700 dark:text-amber-400"> · {inviteSummary.inactive} inaktiv</span>
                          )}
                        </p>
                        <ul className="space-y-1.5">
                          {shownInvites.map((invite) => {
                            const activity = describeActivity(invite, now);
                            return (
                            <li key={invite.id}>
                              <SwipeToDelete
                                onDelete={() => handleDeleteInvite(invite)}
                                busy={deletingInvite === invite.id}
                              >
                                <div className="flex items-center justify-between gap-3 px-3 py-2 pr-10">
                              <div className="min-w-0">
                                <p className="truncate text-sm text-slate-700 dark:text-slate-200">{invite.email}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                  {formatDateTime(invite.createdAt)} erstellt ·{' '}
                                  <span className={activity.tone}>{activity.text}</span>
                                </p>
                                {activity.bar && (
                                  <div
                                    className="mt-1.5 h-1 w-32 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700"
                                    role="progressbar"
                                    aria-valuenow={invite.progress}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                    aria-label="Fortschritt"
                                  >
                                    <div className="h-full rounded-full bg-blue-500" style={{ width: `${invite.progress}%` }} />
                                  </div>
                                )}
                              </div>
                                <div className="flex shrink-0 items-center gap-3">
                                  {invite.sentAt ? (
                                    <button
                                      type="button"
                                      onClick={() => handleOpenInviteMail(invite)}
                                      disabled={openingInviteId === invite.id}
                                      className="text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:underline disabled:opacity-50"
                                    >
                                      nochmal öffnen
                                    </button>
                                  ) : (
                                    <ModernButton
                                      size="sm"
                                      icon={MailIcon}
                                      onClick={() => handleOpenInviteMail(invite)}
                                      disabled={openingInviteId === invite.id}
                                      loading={openingInviteId === invite.id}
                                    >
                                      E-Mail öffnen
                                    </ModernButton>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard?.writeText(invite.link);
                                      toast.success('Link kopiert');
                                    }}
                                    className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                                  >
                                    Link kopieren
                                  </button>
                                </div>
                                </div>
                              </SwipeToDelete>
                            </li>
                            );
                          })}
                        </ul>
                        {invites.length > VISIBLE_INVITES && (
                          <button
                            type="button"
                            onClick={() => setShowAllInvites((prev) => !prev)}
                            className="mt-2 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                          >
                            {showAllInvites
                              ? 'Weniger anzeigen'
                              : `Alle ${invites.length} anzeigen`}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )
              ) : (
                <div className="space-y-5">
                  <FileField
                    label="Fragebogen 2 (HTML)"
                    accept=".html,.htm"
                    filename={htmlFile ? htmlFile.name : removeDocument ? null : record?.filename}
                    placeholder="Noch kein Fragebogen hinterlegt"
                    hint={
                      removeDocument
                        ? 'Wird beim Speichern entfernt. Bestehende Einladungen laufen dann ins Leere.'
                        : undefined
                    }
                    onSelect={handleHtml}
                    onClear={() => {
                      // A picked-but-unsaved file just goes away; a stored one
                      // is marked for removal and only leaves on save.
                      setDocumentReport(null);
                      if (htmlFile) setHtmlFile(null);
                      else setRemoveDocument(true);
                    }}
                    actions={
                      record?.filename && !removeDocument
                        ? [
                            { label: 'Ansehen', onClick: handlePreview },
                            { label: 'Herunterladen', onClick: handleDownloadDocument },
                          ]
                        : null
                    }
                  />

                  {documentReport && <DocumentReport report={documentReport} />}

                  <FileField
                    label="Einladungs-Mail (Markdown)"
                    accept=".md,.markdown,.txt"
                    filename={null}
                    placeholder="…_Email_Einladung_Fragebogen2.md"
                    hint="Optional. Füllt Betreff und Text aus der Datei."
                    onSelect={handleMarkdown}
                    actions={[
                      {
                        label: 'Herunterladen',
                        onClick: handleDownloadMail,
                        disabled: !subject.trim() && !body.trim(),
                      },
                    ]}
                  />


                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                      Betreff
                    </label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className={FIELD}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                      Mailtext
                    </label>
                    <textarea
                      rows={22}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      className={`${FIELD} font-mono text-xs`}
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      <code>{'{Vorname}'}</code> wird beim Versenden ersetzt:{' '}
                      {fillPlaceholders(body, { Vorname: 'Anna' }).split('\n')[0] || '—'}
                    </p>
                  </div>

                  <div className="flex justify-end gap-2">
                    <ModernButton variant="secondary" onClick={onClose}>
                      Abbrechen
                    </ModernButton>
                    <ModernButton onClick={handleSave} disabled={saving} loading={saving}>
                      Speichern
                    </ModernButton>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
